import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { ExamLandingPage } from '@/routes/exam/ExamLandingPage';
import { ExamPage } from '@/routes/exam/ExamPage';
import { InviteLandingPage } from '@/routes/exam/InviteLandingPage';
import { RegisterPage } from '@/routes/exam/RegisterPage';
import { ResultPage } from '@/routes/exam/ResultPage';
import { HealthPage } from '@/routes/HealthPage';
import { HomePage } from '@/routes/HomePage';
import { NotFoundPage } from '@/routes/NotFoundPage';

/**
 * ฝั่งผู้เรียนโหลดตรงๆ (ต้องเบาที่สุด เพราะส่วนใหญ่เปิดบนมือถือผ่านเน็ตมือถือ)
 * ฝั่งแอดมิน lazy ทั้งหมด → ผู้เรียนไม่ต้องโหลด bundle ของหน้าจัดการเลย
 */
/** ประกาศความเป็นส่วนตัว — เปิดเป็นแท็บใหม่ตอนกดลิงก์ ไม่ต้องอยู่ใน bundle หลักของผู้เรียน */
const PrivacyPage = lazy(() =>
  import('@/routes/PrivacyPage').then((module) => ({ default: module.PrivacyPage })),
);

const AdminLayout = lazy(() =>
  import('@/routes/admin/AdminLayout').then((module) => ({ default: module.AdminLayout })),
);
const AdminLoginPage = lazy(() =>
  import('@/routes/admin/AdminLoginPage').then((module) => ({ default: module.AdminLoginPage })),
);
const CoursesPage = lazy(() =>
  import('@/routes/admin/CoursesPage').then((module) => ({ default: module.CoursesPage })),
);
const CourseDetailPage = lazy(() =>
  import('@/routes/admin/CourseDetailPage').then((module) => ({
    default: module.CourseDetailPage,
  })),
);
const QuestionEditorPage = lazy(() =>
  import('@/routes/admin/QuestionEditorPage').then((module) => ({
    default: module.QuestionEditorPage,
  })),
);
const PrintExamSetPage = lazy(() =>
  import('@/routes/admin/PrintExamSetPage').then((module) => ({
    default: module.PrintExamSetPage,
  })),
);
const ParticipantsPage = lazy(() =>
  import('@/routes/admin/ParticipantsPage').then((module) => ({
    default: module.ParticipantsPage,
  })),
);
const OverviewPage = lazy(() =>
  import('@/routes/admin/OverviewPage').then((module) => ({ default: module.OverviewPage })),
);
const AnalyticsPage = lazy(() =>
  import('@/routes/admin/AnalyticsPage').then((module) => ({ default: module.AnalyticsPage })),
);
const ItemAnalysisPage = lazy(() =>
  import('@/routes/admin/ItemAnalysisPage').then((module) => ({
    default: module.ItemAnalysisPage,
  })),
);
const LiveMonitorPage = lazy(() =>
  import('@/routes/admin/LiveMonitorPage').then((module) => ({ default: module.LiveMonitorPage })),
);
const InvitesPage = lazy(() =>
  import('@/routes/admin/InvitesPage').then((module) => ({ default: module.InvitesPage })),
);

const CohortComparePage = lazy(() =>
  import('@/routes/admin/CohortComparePage').then((module) => ({
    default: module.CohortComparePage,
  })),
);

const lazyPage = (children: React.ReactNode) => (
  <Suspense fallback={<div className="p-8 text-center text-slate-600">…</div>}>{children}</Suspense>
);

export const router = createBrowserRouter([
  { path: '/', element: <HomePage /> },
  // หน้าสถานะระบบไว้ให้ผู้ดูแลเปิดดูตอน deploy — ไม่ใช่หน้าแรกของผู้เรียน
  { path: '/health', element: <HealthPage /> },

  { path: '/privacy', element: lazyPage(<PrivacyPage />) },

  { path: '/t/:token', element: <ExamLandingPage /> },
  { path: '/t/:token/register', element: <RegisterPage /> },
  { path: '/t/:token/exam', element: <ExamPage /> },
  { path: '/t/:token/result', element: <ResultPage /> },

  // ลิงก์ส่วนตัวจากรายชื่อที่นำเข้าล่วงหน้า — ไม่มีหน้า register เพราะรู้ตัวตนอยู่แล้ว
  { path: '/i/:token', element: <InviteLandingPage /> },
  { path: '/i/:token/exam', element: <ExamPage /> },
  { path: '/i/:token/result', element: <ResultPage /> },

  {
    path: '/admin/login',
    element: lazyPage(<AdminLoginPage />),
  },
  {
    path: '/admin',
    element: lazyPage(<AdminLayout />),
    children: [
      { index: true, element: <CoursesPage /> },
      { path: 'courses', element: <CoursesPage /> },
      { path: 'compare', element: <CohortComparePage /> },
      { path: 'courses/:id', element: <CourseDetailPage /> },
      { path: 'courses/:id/overview', element: <OverviewPage /> },
      { path: 'courses/:id/analytics', element: <AnalyticsPage /> },
      { path: 'courses/:id/items', element: <ItemAnalysisPage /> },
      { path: 'courses/:id/participants', element: <ParticipantsPage /> },
      { path: 'courses/:id/live', element: <LiveMonitorPage /> },
      { path: 'courses/:id/invites', element: <InvitesPage /> },
      { path: 'exam-sets/:id/edit', element: <QuestionEditorPage /> },
      { path: 'exam-sets/:id/print', element: <PrintExamSetPage /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
